// @flow
import React, {Fragment, PureComponent, Component} from 'react';
import {DateRangePicker} from '@homeaway/react-date-pickers';
import DayAvailability from '../DayAvailability/DayAvailability';
import {media} from '../../utils/breakpoints';
import classNames from 'classnames';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {injectIntl, FormattedMessage} from 'react-intl';
import {SVGIcon} from '@homeaway/react-svg';
import PickerInputLabel from '../PickerInputLabel/PickerInputLabel';
import './ConnectedDateRangePicker.less';
import DateRangePickerSVGDefinitions from './DateRangePickerSVGDefinitions';

import type {IntlShape} from 'react-intl';
import type {State as ReduxState} from '../../reducers';
import type {Element} from 'react';

type MessageDescriptor = {messageId: string, defaultText: string};

type NumericDateOptionsType = 'numeric' | '2-digit';
type LiteralDateOptionsType = 'short' | 'long' | 'narrow';
type DateFormatOptions = NumericDateOptionsType | LiteralDateOptionsType;

export type DateFormatOptionsType = {
    day?: NumericDateOptionsType,
    month?: DateFormatOptions,
    year?: NumericDateOptionsType
};
type OwnProps = {
    calendarArriveIcon?: Element<typeof Component>,
    calendarDepartIcon?: Element<typeof Component>,
    consolidateInputs: boolean,
    danger?: boolean,
    dateFormatOptions: DateFormatOptionsType,
    dateTextDescriptorInput1: MessageDescriptor,
    dateTextDescriptorInput2: MessageDescriptor,
    endDate: string,
    isOpen: boolean,
    startDate: string,
    useFloatingLabel: boolean
};

type IntlProps = {
    intl: IntlShape
};
type StateProps = {
    isMobile: boolean,
    isTouchDevice: boolean,
    fullLocale: string,
    onDateRangeClear: () => void,
    onOpen: () => void
};

type Props = OwnProps & StateProps & IntlProps;

type State = {
    modalTitleDescriptor: ?Object,
    edited: boolean
};

const selectCheckInDescriptor = {
    messageId: 'selectCheckIn',
    defaultText: 'Select a check-in date'
};

const selectCheckOutDescriptor = {
    messageId: 'selectCheckOut',
    defaultText: 'Select a checkout date'
};

const emptyDescriptor = {
    messageId: '',
    defaultText: ''
};

const DEFAULT_PROPS = {
    consolidateInputs: false,
    dateFormatOptions: {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
    },
    dateTextDescriptorInput1: {
        defaultText: 'Check In',
        messageId: 'checkIn'
    },
    dateTextDescriptorInput2: {
        defaultText: 'Check Out',
        messageId: 'checkOut'
    },
    useFloatingLabel: true
};

export class DateRangePickerComponent extends PureComponent<Props, State> {
    static defaultProps = DEFAULT_PROPS;

    constructor(props: Props) {
        super(props);

        this.state = {
            modalTitleDescriptor: null,
            edited: false
        };
    }

    clearButtonLabel = () =>
        this.props.intl.formatMessage({
            id: 'datePickerClearLabel',
            defaultMessage: 'Clear'
        });

    continueButtonLabel = () => {
        const {formatMessage} = this.props.intl;
        return formatMessage({
            id: 'datePickerContinueLabel',
            defaultMessage: 'Continue'
        });
    };

    closeButtonLabel = () =>
        this.props.intl.formatMessage({
            id: 'datePickerCloseLabel',
            defaultMessage: 'Close'
        });

    onSelectionModeChanged = (selectingEndDate: boolean) => {
        if (selectingEndDate) {
            this.setState({
                modalTitleDescriptor: selectCheckOutDescriptor,
                edited: true
            });
        }
    };

    onDateRangePickerOpen = () => {
        this.setState({
            modalTitleDescriptor: selectCheckInDescriptor,
            edited: false
        });
        this.props.onOpen();
    };

    getModalTitle = () => {
        const {startDate, endDate} = this.props;

        if (this.state.edited && startDate && endDate) {
            return null;
        }

        return this.state.modalTitleDescriptor ? (
            <FormattedMessage
                id={this.state.modalTitleDescriptor.messageId}
                defaultMessage={this.state.modalTitleDescriptor.defaultText}
            />
        ) : null;
    };

    onDateRangePickerCleared = () => {
        this.setState({
            modalTitleDescriptor: selectCheckInDescriptor,
            edited: true
        });
        this.props.onDateRangeClear();
    };

    render() {
        let {
            calendarArriveIcon,
            calendarDepartIcon,
            danger,
            dateFormatOptions,
            dateTextDescriptorInput1,
            dateTextDescriptorInput2,
            isMobile,
            isOpen,
            isTouchDevice,
            useFloatingLabel,
            fullLocale,
            ...rest
        } = this.props;

        const {startDate, endDate} = this.props;

        const displayYearFirst = fullLocale.substr(0, 5) === 'ja_JP';

        // Null out the label when our inputs are filled to disable the
        // "floating label" effect
        if (!useFloatingLabel && startDate) {
            dateTextDescriptorInput1 = emptyDescriptor;
        }

        if (!useFloatingLabel && endDate) {
            dateTextDescriptorInput2 = emptyDescriptor;
        }

        const inputLabelClassNames = classNames({
            'text-danger': danger
        });

        return (
            <DateRangePicker
                {...rest}
                className={classNames('DateRangePicker', {
                    'DateRangePicker--gt': !isMobile,
                    'DateRangePicker--danger': danger,
                    'DateRangePicker__arrow-right':
                        !!startDate && !endDate && !isMobile
                })}
                dateFormatOptions={dateFormatOptions}
                disableTextInput
                inputIcon1={<SVGIcon id="CALENDAR_ARRIVE__16" />}
                inputIcon2={<SVGIcon id="CALENDAR_DEPART__16" />}
                inputLabel1={
                    <PickerInputLabel
                        textClass={inputLabelClassNames}
                        {...dateTextDescriptorInput1}
                    />
                }
                inputLabel2={
                    <PickerInputLabel
                        textClass={inputLabelClassNames}
                        {...dateTextDescriptorInput2}
                    />
                }
                centered={false}
                clearButtonLabel={this.clearButtonLabel()}
                continueButtonLabel={this.continueButtonLabel()}
                closeButtonLabel={this.closeButtonLabel()}
                edapLabel="search"
                fadeOutDuration={0}
                id="date-range-picker"
                inputName1="checkin"
                inputName2="checkout"
                isMobile={isMobile}
                showArrow={false}
                showContinueButton={
                    (!!startDate && !!endDate) || (!startDate && !endDate)
                }
                dayTemplateComponent={DayAvailability}
                modalTitle={isMobile ? this.getModalTitle() : null}
                locale={fullLocale}
                isOpen={isOpen}
                onOpen={this.onDateRangePickerOpen}
                onSelectionModeChange={this.onSelectionModeChanged}
                onDateRangeClear={this.onDateRangePickerCleared}
                displayYearFirst={displayYearFirst}
            />
        );
    }
}

const mapStateToProps = ({
    system: {fullLocale},
    viewport: {breakpoint}
}: ReduxState) => {
    const isMobile = !media(breakpoint, {minWidth: 'SM_MIN'});
    return {
        isMobile,
        isTouchDevice: isMobile || !media(breakpoint, {minWidth: 'MD_MIN'}),
        fullLocale
    };
};

export const ConnectedDateRangePicker = compose(
    injectIntl,
    connect(
        mapStateToProps,
        null
    )
)(DateRangePickerComponent);

const ConnectedDateRangePickerWithSVGDefs = (props: OwnProps) => (
    <Fragment>
        <DateRangePickerSVGDefinitions />
        <ConnectedDateRangePicker {...props} />
    </Fragment>
);
ConnectedDateRangePickerWithSVGDefs.defaultProps = DEFAULT_PROPS;

export default ConnectedDateRangePickerWithSVGDefs;
